import {
  Box,
  Button,
  Flex,
  Icon,
  Stack,
  IconButton,
  Text,
} from '@chakra-ui/react';
import {
  MetaTags,
  PageHeader,
  TeamMenu,
  useQueryState,
  useToastHook,
  FileUpload,
  useDebounce,
  SearchBox,
  SearchInput,
  eliminateHtmlTags,
  SearchIconContainer,
  StyledSearchIcon,
  Loader,
  AppTooltip,
} from '@devd-client/devd/components';
import { FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CardTable from '../components/CardTable';
import { useTeamHomeDetails, QUERY_KEYS } from '../apis';
import { useEffect, useState } from 'react';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import { TbUpload, TbDownload } from 'react-icons/tb';
import { binaryApiClient } from '@devd-client/api';
import { getToken, getOrgId } from '@devd-client/devd/auth';
import { TEAM_DETAILS_QUERY_KEYS } from '@devd-client/devd/team-details';
import { useDashboardStore } from '@devd-client/devd/components';

export function DevdTeams() {
  const navigate = useNavigate();
  const [newToast] = useToastHook();
  const [AppState, dispatch] = useAuth();
  const plan = AppState?.user?.dto?.plan;
  const [team, setTeam] = useQueryState('team');
  const [uploadTeamsFile, setUploadTeamsFile] = useState<any>(false);
  const debouncedTeam = useDebounce(team, 300);
  const [teamLength, setTeamLength] = useState(0);
  const [pending, setPending] = useState(false);

  const { data: teamHomedata } = useTeamHomeDetails();
  const helpDocLink = teamHomedata?.dto?.helpDoc;
  const { selectedTeam, setSelectedTeam } = useDashboardStore();

  const download = function (data: any) {
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window?.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `teams_${getOrgId()}.xlsx`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window?.URL?.revokeObjectURL(url);
  };

  const handleDownloadTeams = async () => {
    setPending(true);
    const orgId = getOrgId();

    if (!orgId) {
      newToast({
        message: 'Organization ID not found',
        status: 'error',
      });
      setPending(false);
      return;
    }

    binaryApiClient(`/v2/team/${orgId}/export`, {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    })
      .then((blob: any) => {
        setPending(false);
        download(blob);
        newToast({
          message: 'Teams downloaded successfully',
          status: 'success',
        });
      })
      .catch((err) => {
        setPending(false);
        newToast({
          message: 'Failed to download teams',
          status: 'error',
        });
      });
  };

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );
  }, []);

  useEffect(() => {
    if (typeof selectedTeam !== 'undefined') {
      selectedTeam.label === 'Organisation'
        ? setTeam('Organisation')
        : setTeam(selectedTeam.value);
    }
  }, [selectedTeam, setTeam]);

  return (
    <>
      <MetaTags title="Teams | Settings | Devdynamics" />
      <Box
        display={'flex'}
        flexDirection={'column'}
        width={'100%'}
        minHeight={'90vh'}
        marginBottom={'50px'}
      >
        {uploadTeamsFile ? (
          <FileUpload
            setUploadFile={setUploadTeamsFile}
            uploadLink={`/v1/team/org/${getOrgId()}/import`}
            queryKey={{
              teams: QUERY_KEYS.teams,
              teamList: TEAM_DETAILS_QUERY_KEYS.teamList,
            }}
          />
        ) : (
          <Stack spacing={6}>
            <PageHeader
              headingText="Teams"
              helpDocLink={helpDocLink}
              component={
                <Flex gap={2}>
                  <Box
                    border={'1px solid #626266'}
                    borderRadius={'4px'}
                    p={'1'}
                    paddingLeft={2}
                    paddingRight={1}
                    onClick={() => {
                      setUploadTeamsFile(true);
                    }}
                  >
                    <AppTooltip
                      content={
                        <Text
                          fontSize="sm"
                          color="text.secondary"
                          fontWeight="normal"
                          fontFamily="heading"
                        >
                          Upload Teams
                        </Text>
                      }
                      placement="bottom"
                      trigger="hover"
                    >
                      <IconButton
                        aria-label="upload-teams"
                        icon={<TbUpload size={20} color="#626266" />}
                        variant="unstyled"
                        size={'xs'}
                      />
                    </AppTooltip>
                  </Box>

                  <Box
                    border={'1px solid #626266'}
                    borderRadius={'4px'}
                    p={'1'}
                    paddingLeft={2}
                    paddingRight={1}
                    onClick={handleDownloadTeams}
                  >
                    <AppTooltip
                      content={
                        <Text
                          fontSize="sm"
                          color="text.secondary"
                          fontWeight="normal"
                          fontFamily="heading"
                        >
                          Download Teams
                        </Text>
                      }
                      placement="bottom"
                      trigger="hover"
                    >
                      <IconButton
                        aria-label="download-teams"
                        icon={<TbDownload size={20} color="#626266" />}
                        variant="unstyled"
                        size={'xs'}
                        isLoading={pending}
                      />
                    </AppTooltip>
                  </Box>

                  <Button
                    ml={'auto'}
                    mt={'auto'}
                    background={'#FFFFFF'}
                    borderRadius={'4px'}
                    variant="primary"
                    fontSize={'sm'}
                    fontWeight={'medium'}
                    h={'36px'}
                    onClick={() => {
                      if (
                        AppState?.user?.dto?.teamAllowed &&
                        (plan === 'Free' || plan === 'Trial') &&
                        teamLength >= AppState?.user?.dto?.teamAllowed
                      ) {
                        newToast({
                          message:
                            'Upgrade your plan to add more teams, or contact Devdynamics support',
                          status: 'error',
                        });
                      } else {
                        window.analytics.track('team_created', {
                          status: 'init',
                        });
                        navigate('/settings/teams/add-team', {
                          state: { newTeam: true },
                        });
                      }
                    }}
                  >
                    <Icon as={FaPlus} mr={2} />
                    New Team
                  </Button>
                </Flex>
              }
            />

            <Flex>
              <TeamMenu
                selectedTeam={selectedTeam}
                handleSelectedTeam={setSelectedTeam}
                AuthActionTypes={AuthActionTypes}
                getUser={getUser}
                appState={AppState}
                dispatch={dispatch}
                hideShowDetails
                mb={2}
              />
            </Flex>

            <CardTable
              team={
                team === ''
                  ? selectedTeam.value
                  : debouncedTeam === 'Organisation'
                  ? selectedTeam.value
                  : debouncedTeam
              }
              teamLength={teamLength}
              plan={plan}
              appState={AppState}
              setTeamLength={setTeamLength}
            />
          </Stack>
        )}
      </Box>
    </>
  );
}

export default DevdTeams;
